import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { paths } from "@/types/api";

type LandlordBody =
  paths["/api/v1/landlords"]["post"]["requestBody"]["content"]["application/json"];
type LandlordUpdateBody =
  paths["/api/v1/landlords/{landlord_id}"]["patch"]["requestBody"]["content"]["application/json"];
type TenantBody =
  paths["/api/v1/tenants"]["post"]["requestBody"]["content"]["application/json"];
type TenantUpdateBody =
  paths["/api/v1/tenants/{tenant_id}"]["patch"]["requestBody"]["content"]["application/json"];
type GuarantorBody =
  paths["/api/v1/guarantors"]["post"]["requestBody"]["content"]["application/json"];
type GuarantorUpdateBody =
  paths["/api/v1/guarantors/{guarantor_id}"]["patch"]["requestBody"]["content"]["application/json"];

export const partyKeys = {
  landlords: ["parties", "landlords"] as const,
  tenants: ["parties", "tenants"] as const,
  guarantors: ["parties", "guarantors"] as const,
};

export function useLandlords() {
  return useQuery({
    queryKey: partyKeys.landlords,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/landlords", {});
      if (error || !data) throw new Error("Failed to load landlords");
      return data;
    },
  });
}

export function useLandlord(id: string | undefined) {
  return useQuery({
    queryKey: [...partyKeys.landlords, id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/landlords/{landlord_id}", {
        params: { path: { landlord_id: id! } },
      });
      if (error || !data) throw new Error("Failed to load landlord");
      return data;
    },
  });
}

export function useTenant(id: string | undefined) {
  return useQuery({
    queryKey: [...partyKeys.tenants, id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/tenants/{tenant_id}", {
        params: { path: { tenant_id: id! } },
      });
      if (error || !data) throw new Error("Failed to load tenant");
      return data;
    },
  });
}

export function useCreateLandlord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await apiClient.POST("/api/v1/landlords", {
        body: body as LandlordBody,
      });
      if (error || !data) throw new Error("Failed to create landlord");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: partyKeys.landlords }),
  });
}

export function useUpdateLandlord(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await apiClient.PATCH(
        "/api/v1/landlords/{landlord_id}",
        { params: { path: { landlord_id: id } }, body: body as LandlordUpdateBody },
      );
      if (error || !data) throw new Error("Failed to update landlord");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: partyKeys.landlords });
      qc.invalidateQueries({ queryKey: [...partyKeys.landlords, id] });
    },
  });
}

export function useTenants() {
  return useQuery({
    queryKey: partyKeys.tenants,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/tenants", {});
      if (error || !data) throw new Error("Failed to load tenants");
      return data;
    },
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await apiClient.POST("/api/v1/tenants", {
        body: body as TenantBody,
      });
      if (error || !data) throw new Error("Failed to create tenant");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: partyKeys.tenants }),
  });
}

export function useUpdateTenant(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await apiClient.PATCH("/api/v1/tenants/{tenant_id}", {
        params: { path: { tenant_id: id } },
        body: body as TenantUpdateBody,
      });
      if (error || !data) throw new Error("Failed to update tenant");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: partyKeys.tenants });
      qc.invalidateQueries({ queryKey: [...partyKeys.tenants, id] });
    },
  });
}

export function useGuarantors() {
  return useQuery({
    queryKey: partyKeys.guarantors,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/guarantors", {});
      if (error || !data) throw new Error("Failed to load guarantors");
      return data;
    },
  });
}

export function useGuarantor(id: string | undefined) {
  return useQuery({
    queryKey: [...partyKeys.guarantors, id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/guarantors/{guarantor_id}", {
        params: { path: { guarantor_id: id! } },
      });
      if (error || !data) throw new Error("Failed to load guarantor");
      return data;
    },
  });
}

export function useCreateGuarantor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await apiClient.POST("/api/v1/guarantors", {
        body: body as GuarantorBody,
      });
      if (error || !data) throw new Error("Failed to create guarantor");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: partyKeys.guarantors }),
  });
}

export function useUpdateGuarantor(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await apiClient.PATCH(
        "/api/v1/guarantors/{guarantor_id}",
        { params: { path: { guarantor_id: id } }, body: body as GuarantorUpdateBody },
      );
      if (error || !data) throw new Error("Failed to update guarantor");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: partyKeys.guarantors });
      qc.invalidateQueries({ queryKey: [...partyKeys.guarantors, id] });
    },
  });
}
